import { AuthGuard } from '@/common/auth/guards/auth.guard';
import { LayoutAuthComponent } from '@/layout/auth/auth.component';
import { LayoutPublicComponent } from '@/layout/public/public.component';
import { PublicGuard } from '@/common/auth/guards/public.guard';
import { ROLES } from '@/constants/roles.constants';
import { RoleGuard } from '@/common/guards/role.guard';
import { Routes } from '@angular/router';
import { inject } from '@angular/core';
import { SessionService } from '@/common/auth/auth-session.service';

export const routes: Routes = [
	/**
	 * Public routes
	 */
	{
		path: '',
		component: LayoutPublicComponent,
		children: [
			{
				path: '',
				redirectTo: 'login',
				pathMatch: 'full',
			},
			{
				path: 'login',
				loadComponent: () => import('@/pages/login/login.component').then(m => m.LoginComponent),
			},
			{
				path: 'login/link/:token',
				loadComponent: () => import('@/pages/loginLink/loginLink.component').then(m => m.LoginLinkComponent),
			},
			{
				path: 'email',
				children: [
					{
						path: 'link',
						loadComponent: () => import('@/pages/email/link/link.component').then(m => m.LinkComponent),
					},
					{
						path: 'verify/:token',
						loadComponent: () => import('@/pages/email/verify/verify.component').then(m => m.VerifyComponent),
					},
				],
			},
			{
				path: 'password',
				children: [
					{
						path: 'forgot',
						loadComponent: () => import('@/pages/password/forgot/forgot.component').then(m => m.ForgotComponent),
					},
					{
						path: 'reset/:token',
						loadComponent: () => import('@/pages/password/reset/reset.component').then(m => m.ResetComponent),
					},
				],
			},
		],
		canActivate: [PublicGuard],
	},
	/**
	 * Authenticated routes
	 */
	{
		path: '',
		component: LayoutAuthComponent,
		children: [
			{
				path: 'logout',
				loadComponent: () => import('@/pages/logout/logout.component').then(m => m.LogoutComponent),
			},
			{
				path: 'dashboard',
				loadComponent: () => import('@/pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
			},
			{
				path: 'users',
				children: [
					{
						path: '',
						loadComponent: () => import('@/pages/user/user.component').then(m => m.UserComponent),
					},
					{
						path: 'create',
						loadComponent: () => import('@/pages/user/create-user/create-user.component').then(m => m.CreateUserComponent),
					},
					{
						path: ':id',
						loadComponent: () => import('@/pages/user/show-user/show-user.component').then(m => m.ShowUserComponent),
					},
				],
			},
			{
				path: 'projects',
				children: [
					{
						path: '',
						loadComponent: () => import('@/pages/project/project.component').then(m => m.ProjectComponent),
					},
					{
						path: 'create',
						loadComponent: () => import('@/pages/project/create-project/create-project.component').then(m => m.CreateProjectComponent),
					},
					{
						path: ':id',
						loadComponent: () => import('@/pages/project/show-project/show-project.component').then(m => m.ShowProjectComponent),
					},
					{
						path: ':id/sync-health',
						loadComponent: () => import('@/pages/project/sync-health/sync-health.component').then(m => m.SyncHealthComponent),
					},
				],
			},
			{
				path: 'person-mappings',
				loadComponent: () => import('@/pages/person-mapping/person-mapping.component').then(m => m.PersonMappingComponent),
			},
			{
				path: 'tasks',
				children: [
					{
						path: '',
						loadComponent: () => import('@/pages/task/task.component').then(m => m.TaskComponent),
					},
					{
						path: ':id',
						loadComponent: () => import('@/pages/task/show-task/show-task.component').then(m => m.ShowTaskComponent),
					},
				],
			},
			{
				path: 'profile',
				loadComponent: () => import('@/pages/profile/profile.component').then(m => m.ProfileComponent),
			},
			{
				path: 'settings',
				loadComponent: () => import('@/pages/settings/settings.component').then(m => m.SettingsComponent),
				// Admins come in for the alert channel only (task 9.1); the roles pages stay with superadmins
				canActivate: [RoleGuard],
				data: {roles: [ROLES.SUPERADMIN, ROLES.ADMIN]},
				children: [
					{
						path: 'roles',
						canActivate: [RoleGuard],
						data: {roles: [ROLES.SUPERADMIN], refused: '/settings/alerts'},
						children: [
							{
								path: '',
								loadComponent: () => import('@/pages/role/role.component').then(m => m.RoleComponent),
							},
							{
								path: 'create',
								loadComponent: () => import('@/pages/role/role-create/role-create.component').then(m => m.RoleCreateComponent),
							},
							{
								path: ':id',
								loadComponent: () => import('@/pages/role/role-editor/role-editor.component').then(m => m.RoleEditorComponent),
							},
						],
					},
					{
						path: 'alerts',
						loadComponent: () => import('@/pages/settings/sync-alert-setting/sync-alert-setting.component').then(m => m.SyncAlertSettingComponent),
					},
					{
						path: '',
						redirectTo: () => inject(SessionService).isSuperAdmin() ? 'roles' : 'alerts',
						pathMatch: 'full'
					},
				]
			}
		],
		canActivate: [AuthGuard],
	},
	/**
	 * Not found
	 */
	{
		path: '**',
		loadComponent: () => import('@/pages/page-not-found/page-not-found.component').then(m => m.PageNotFoundComponent),
	},
];
